import { Howl } from 'howler';
import { useRef, useState, useEffect, useCallback } from 'react';
import { initializeAudioContext } from '@/lib/audio-context';

interface UseAudioOptions {
  onEnd?: () => void;
  onLoad?: () => void;
  onLoadError?: (src: string) => void;
  loop?: boolean;
}

interface UseAudioReturn {
  // Playback state
  isPlaying: boolean;
  isLoaded: boolean;

  // Position
  currentTime: number; // Seconds (e.g., 83.5 = 1:23.5)
  duration: number; // Total seconds (e.g., 225 = 3:45)

  // Controls
  play: () => void;
  pause: () => void;
  stop: () => void;
  seek: (seconds: number) => void;
  skipForward: (seconds?: number) => void;
  skipBackward: (seconds?: number) => void;
  setVolume: (volume: number) => void; // 0.0 to 1.0

  // Formatting helpers
  formatTime: (seconds: number) => string; // Converts 83.5 to "1:24"
  getProgress: () => number; // Returns progress as ratio (0.0 to 1.0)
}

/**
 * Audio playback hook using Howler.js
 *
 * Independent controls - audio does NOT control timer
 * User can skip/scrub audio while timer continues unaffected
 *
 * @param src - Audio source (single path or array of paths for fallback)
 */
export function useAudio(
  src: string | string[],
  options: UseAudioOptions = {}
): UseAudioReturn {
  const { onEnd, onLoad, onLoadError, loop = false } = options;
  const howlRef = useRef<Howl | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  // Store callbacks in refs to avoid recreating Howl when they change
  const onEndRef = useRef(onEnd);
  const onLoadRef = useRef(onLoad);
  const onLoadErrorRef = useRef(onLoadError);
  useEffect(() => {
    onEndRef.current = onEnd;
    onLoadRef.current = onLoad;
    onLoadErrorRef.current = onLoadError;
  }, [onEnd, onLoad, onLoadError]);

  // Initialize Howl instance
  useEffect(() => {
    // Don't create Howl if src is empty
    if (!src) {
      setIsLoaded(false);
      return;
    }

    // Get saved volume or use default
    const savedVolume = localStorage.getItem('audioVolume');
    const initialVolume = savedVolume ? parseFloat(savedVolume) : 0.7;

    // Detect formats from file extension(s)
    const getFormatsFromSrc = (source: string | string[]): string[] => {
      const sources = Array.isArray(source) ? source : [source];
      const formats: string[] = [];

      sources.forEach(path => {
        const ext = path.toLowerCase().split('.').pop();
        if (ext === 'mp3' && !formats.includes('mp3')) formats.push('mp3');
        if (ext === 'wav' && !formats.includes('wav')) formats.push('wav');
        if (ext === 'ogg' && !formats.includes('ogg')) formats.push('ogg');
      });

      // Default fallback if no recognized format found
      return formats.length > 0 ? formats : ['mp3', 'wav'];
    };

    // Cleanup previous instance if it exists
    // CRITICAL: Must stop before unload to properly release pool slot
    if (howlRef.current) {
      howlRef.current.stop();
      howlRef.current.off();
      howlRef.current.unload();
      howlRef.current = null;
    }

    // IMPORTANT: For multi-source with different files (WAV/MP3 quality levels),
    // we need to use the FIRST source only. Howler's multi-source is for format
    // compatibility, not quality levels. We'll handle fallback in onloaderror.
    const primarySrc = Array.isArray(src) ? src[0] : src;
    const fallbackSrc = Array.isArray(src) && src.length > 1 ? src[1] : null;

    howlRef.current = new Howl({
      src: [primarySrc],
      loop,
      volume: initialVolume,
      html5: true, // Use HTML5 Audio for better performance
      format: getFormatsFromSrc(primarySrc), // Detect format from primary source

      onload: () => {
        setIsLoaded(true);
        setDuration(howlRef.current?.duration() || 0);
        // Notify parent that track loaded successfully
        onLoadRef.current?.();
      },

      onloaderror: (_id, error) => {
        console.error('🔊 AUDIO ERROR: Primary audio source failed to load');
        console.error('  Error details:', error);
        console.error('  Source URL:', primarySrc);
        console.error('  Timestamp:', new Date().toISOString());

        // If we have a fallback source (e.g., MP3 after WAV failed), try it
        if (fallbackSrc) {
          console.log('🔄 Attempting fallback source:', fallbackSrc);

          // CRITICAL: Unload failed instance first
          if (howlRef.current) {
            howlRef.current.off();
            howlRef.current.unload();
            howlRef.current = null;
          }

          // Create new Howl with fallback source
          howlRef.current = new Howl({
            src: [fallbackSrc],
            loop,
            volume: initialVolume,
            html5: true,
            format: getFormatsFromSrc(fallbackSrc),

            onload: () => {
              setIsLoaded(true);
              setDuration(howlRef.current?.duration() || 0);
              onLoadRef.current?.();
            },

            onloaderror: (_id, fallbackError) => {
              console.error(
                '🔊 AUDIO ERROR: Fallback audio source also failed to load'
              );
              console.error('  Error details:', fallbackError);
              console.error('  Fallback URL:', fallbackSrc);
              console.error('  Timestamp:', new Date().toISOString());
              setIsLoaded(false);
              if (howlRef.current) {
                howlRef.current.off();
                howlRef.current.unload();
                howlRef.current = null;
              }
              // Both sources failed - notify parent to skip
              onLoadErrorRef.current?.(primarySrc);
            },

            onplay: () => setIsPlaying(true),
            onpause: () => setIsPlaying(false),
            onstop: () => {
              setIsPlaying(false);
              setCurrentTime(0);
            },
            onend: () => {
              setIsPlaying(false);
              setCurrentTime(0);
              onEndRef.current?.();
            },
          });
        } else {
          // No fallback - just unload and notify parent
          setIsLoaded(false);
          if (howlRef.current) {
            howlRef.current.off();
            howlRef.current.unload();
            howlRef.current = null;
          }
          onLoadErrorRef.current?.(primarySrc);
        }
      },

      onplay: () => {
        setIsPlaying(true);
      },

      onpause: () => {
        setIsPlaying(false);
      },

      onstop: () => {
        setIsPlaying(false);
        setCurrentTime(0);
      },

      onend: () => {
        setIsPlaying(false);
        setCurrentTime(0);
        onEndRef.current?.();
      },
    });

    // Cleanup on unmount or when src/loop changes
    return () => {
      if (howlRef.current) {
        howlRef.current.stop();
        howlRef.current.off();
        howlRef.current.unload();
        howlRef.current = null;
      }
    };
  }, [src, loop]); // Removed onEnd from dependencies

  // Update current time periodically while playing
  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      const position = howlRef.current?.seek() as number;
      if (typeof position === 'number') {
        setCurrentTime(position);
      }
    }, 100); // Update 10 times per second for smooth scrubber

    return () => clearInterval(interval);
  }, [isPlaying]);

  // Handle audio device changes (e.g., switching from speakers to AirPods)
  useEffect(() => {
    const handleDeviceChange = () => {
      console.log('🔊 Audio device changed - checking playback state');

      // If we should be playing, verify Howler is actually playing
      if (isPlaying && howlRef.current) {
        // Small delay to let the browser reconnect to new device
        setTimeout(() => {
          const actuallyPlaying = howlRef.current?.playing();

          if (!actuallyPlaying) {
            console.log(
              '🔄 Audio stopped after device change - resuming playback'
            );
            howlRef.current?.play();
          }
        }, 150);
      }
    };

    // Listen for device changes (requires HTTPS or localhost)
    if (navigator.mediaDevices) {
      navigator.mediaDevices.addEventListener(
        'devicechange',
        handleDeviceChange
      );
      return () => {
        navigator.mediaDevices.removeEventListener(
          'devicechange',
          handleDeviceChange
        );
      };
    }
  }, [isPlaying]);

  // Handle audio context suspension (Safari/iOS, visibility changes)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isPlaying) {
        // Resume audio context if suspended (common on Safari/iOS)
        const ctx = (window as any).Howler?.ctx;
        if (ctx && ctx.state === 'suspended') {
          console.log('🔄 Resuming suspended audio context');
          ctx.resume().catch((err: Error) => {
            console.error('Failed to resume audio context:', err);
          });
        }

        // Also verify Howler is still playing
        const actuallyPlaying = howlRef.current?.playing();
        if (!actuallyPlaying && howlRef.current) {
          console.log('🔄 Resuming playback after visibility change');
          howlRef.current.play();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isPlaying]);

  // Control functions
  const play = useCallback(() => {
    // Initialize AudioContext on first play (browser requirement)
    initializeAudioContext();
    howlRef.current?.play();
  }, []);

  const pause = useCallback(() => {
    howlRef.current?.pause();
  }, []);

  const stop = useCallback(() => {
    howlRef.current?.stop();
  }, []);

  const seek = useCallback((seconds: number) => {
    howlRef.current?.seek(seconds);
    setCurrentTime(seconds);
  }, []);

  const skipForward = useCallback(
    (seconds: number = 10) => {
      const current = (howlRef.current?.seek() as number) || 0;
      const newPosition = Math.min(current + seconds, duration);
      seek(newPosition);
    },
    [duration, seek]
  );

  const skipBackward = useCallback(
    (seconds: number = 10) => {
      const current = (howlRef.current?.seek() as number) || 0;
      const newPosition = Math.max(current - seconds, 0);
      seek(newPosition);
    },
    [seek]
  );

  const setVolume = useCallback((volume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    howlRef.current?.volume(clampedVolume);
    // Save to localStorage for persistence
    localStorage.setItem('audioVolume', clampedVolume.toString());
  }, []);

  // Format time as M:SS or MM:SS
  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Get progress as a ratio (0.0 to 1.0)
  const getProgress = useCallback((): number => {
    if (duration === 0) return 0;
    return currentTime / duration;
  }, [currentTime, duration]);

  return {
    isPlaying,
    isLoaded,
    currentTime,
    duration,
    play,
    pause,
    stop,
    seek,
    skipForward,
    skipBackward,
    setVolume,
    formatTime,
    getProgress,
  };
}
